"use client";

/**
 * CoverDesigner — the interactive artwork layer for the track/release page cover.
 *
 * Renders (for ALL users):
 *   • the audio-reactive FX layer (off by default — driven by the doc's
 *     `artworkFx` / `artworkFxIntensity` / `artworkFxColor` fields, the SAME
 *     fields the media player + Media Vault edit form already use), and
 *   • any saved draggable text layers (`coverTextLayers`).
 *
 * For super-admins / creators (`canEdit`) it adds an on-cover "Design" mode:
 *   • pick / program the animated vector (or other) FX + intensity + colour,
 *   • add text layers and DRAG them anywhere on the cover, restyle them
 *     (size / colour / weight / font / rotation), delete them.
 * Everything autosaves to Firestore (`setDoc … {merge:true}`) — no separate
 * save button to forget. Positions are stored as percentages so they scale with
 * the cover on every screen; font size uses `cqw` (container-query width).
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { doc, setDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { AudioReactiveArtwork, ARTWORK_FX, type ArtworkFxType } from "@/components/visual/audio-reactive-artwork";
import { ArtworkFxControl, useArtworkFx, resolveFx } from "@/components/visual/artwork-fx-control";
import { useAvatarReactivity } from "@/hooks/use-avatar-reactivity";
import { uploadFile } from "@/services/storage";
import { cn } from "@/lib/utils";
import { Wand2, Type as TypeIcon, Trash2, Check, Plus, X, ImagePlus, Loader2, Pencil } from "lucide-react";

export interface CoverTextLayer {
  id: string;
  text: string;
  xPct: number;   // centre X, 0..100 (of cover width)
  yPct: number;   // centre Y, 0..100 (of cover height)
  sizePct: number; // font size in cqw (~2..40)
  color: string;
  weight: number;  // 400 | 700 | 900
  align: "left" | "center" | "right";
  font: "sans" | "serif" | "mono" | "display";
  shadow: boolean;
  rotation: number; // deg
}

/** An imported sprite / SVG / raster image placed on the cover. */
export interface CoverImageLayer {
  id: string;
  url: string;
  xPct: number;   // centre X, 0..100
  yPct: number;   // centre Y, 0..100
  wPct: number;   // width as % of cover width (height auto)
  rotation: number; // deg
  opacity: number; // 0..1
  /** Audio-reactive motion — how this image moves to the music. */
  motion?: "none" | "woofer" | "bounce" | "sway" | "shake" | "pulse";
}

/** Per-mode transform/opacity driven by the shared --bb-* CSS vars (written
 *  by the avatar-reactivity loop: log-spaced bands 0=sub … 7=air). Gordon:
 *  "woofer FX on top of images, make images move to music in different ways." */
const IMAGE_MOTIONS: { id: NonNullable<CoverImageLayer["motion"]>; label: string; transform: string; opacity?: string }[] = [
  { id: "none",   label: "Still",   transform: "" },
  { id: "woofer", label: "Woofer",  transform: " scale(calc(1 + var(--bb-band-0, 0) * 0.35 + var(--bb-band-1, 0) * 0.1))" },
  { id: "bounce", label: "Bounce",  transform: " translateY(calc(var(--bb-band-1, 0) * -14%))" },
  { id: "sway",   label: "Sway",    transform: " rotate(calc((var(--bb-band-3, 0.25) - 0.25) * 26deg))" },
  { id: "shake",  label: "Shake",   transform: " translate(calc((var(--bb-band-6, 0.25) - 0.25) * 6%), calc((var(--bb-band-2, 0.25) - 0.25) * -6%))" },
  // opacity holds the INNER expression (multiplied with the layer's own
  // opacity inside one calc()) — only Pulse uses it.
  { id: "pulse",  label: "Pulse",   transform: " scale(calc(1 + var(--bb-energy, 0) * 0.12))", opacity: "(0.35 + var(--bb-energy, 0) * 0.65)" },
];

const FONT_STACKS: Record<CoverTextLayer["font"], string> = {
  sans: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
  mono: 'ui-monospace, Menlo, Consolas, monospace',
  display: '"Arial Black", Impact, Haettenschweiler, sans-serif',
};

const SWATCHES = ["#ffffff", "#000000", "#ff3366", "#ffd400", "#00e0a4", "#3b82f6", "#a855f7", "#f97316"];

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
// Deterministic id (no Math.random / Date.now needed for uniqueness within a doc).
const makeId = (seed: number) => `t${seed.toString(36)}${(seed * 2654435761 % 46656).toString(36)}`;

interface Props {
  mediaId: string;
  collectionName: "works" | "releases";
  canEdit: boolean;
  /** Opens the track/release edit window — shown as a pencil beside Design. */
  onEdit?: () => void;
  editLabel?: string;
  initialFx?: { effect: string; intensity: number; color: string };
  initialTextLayers?: CoverTextLayer[];
  initialImageLayers?: CoverImageLayer[];
  /** Cover art URL — image-warping FX (Woofer zoom, RGB Glitch slices) need
   *  the actual artwork pixels; without it those effects draw nothing. */
  coverUrl?: string;
}

export function CoverDesigner({ mediaId, collectionName, canEdit, initialFx, initialTextLayers, initialImageLayers, coverUrl, onEdit, editLabel = "Edit" }: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Feed the shared audio-reactivity CSS vars (--bb-energy, --bb-band-N…)
  // onto the cover root so image-layer motion modes can consume them.
  useAvatarReactivity(rootRef);
  const [fx, setFx] = useState({
    effect: (initialFx?.effect || "off") as string,
    intensity: initialFx?.intensity ?? 1,
    color: initialFx?.color || "",
  });
  const [layers, setLayers] = useState<CoverTextLayer[]>(() =>
    Array.isArray(initialTextLayers) ? initialTextLayers.map(sanitizeLayer) : []
  );
  const [imgLayers, setImgLayers] = useState<CoverImageLayer[]>(() =>
    Array.isArray(initialImageLayers) ? initialImageLayers.map(sanitizeImageLayer) : []
  );
  const [uploadingImg, setUploadingImg] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [design, setDesign] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(false);

  // ── Autosave (debounced) — persists FX + text + image layers to the doc ─────
  const dirtyRef = useRef(false);
  useEffect(() => {
    if (!dirtyRef.current) return; // skip the initial mount
    const t = setTimeout(async () => {
      try {
        await setDoc(
          doc(db, collectionName, mediaId),
          {
            artworkFx: fx.effect,
            artworkFxIntensity: fx.intensity,
            artworkFxColor: fx.color,
            coverTextLayers: layers,
            coverImageLayers: imgLayers,
          },
          { merge: true }
        );
        setSaveError(false);
        setSaved(true);
        setTimeout(() => setSaved(false), 1200);
      } catch (err) {
        console.error("CoverDesigner save failed", err);
        setSaveError(true);
      }
    }, 700);
    return () => clearTimeout(t);
  }, [fx, layers, imgLayers, collectionName, mediaId]);

  const mutate = useCallback(<T,>(fn: () => T): T => {
    dirtyRef.current = true;
    return fn();
  }, []);

  const patchLayer = (id: string, patch: Partial<CoverTextLayer>) =>
    mutate(() => setLayers(prev => prev.map(l => (l.id === id ? { ...l, ...patch } : l))));

  const addLayer = () =>
    mutate(() => {
      const id = makeId(layers.length + 1 + (layers.reduce((a, l) => a + l.text.length, 0)));
      const nl: CoverTextLayer = {
        id, text: "New text", xPct: 50, yPct: 50, sizePct: 9,
        color: "#ffffff", weight: 900, align: "center", font: "display",
        shadow: true, rotation: 0,
      };
      setLayers(prev => [...prev, nl]);
      setSelectedId(id);
    });

  const removeLayer = (id: string) =>
    mutate(() => {
      setLayers(prev => prev.filter(l => l.id !== id));
      setSelectedId(cur => (cur === id ? null : cur));
    });

  // ── Image / SVG / sprite layers ─────────────────────────────────────────────
  const patchImg = (id: string, patch: Partial<CoverImageLayer>) =>
    mutate(() => setImgLayers(prev => prev.map(l => (l.id === id ? { ...l, ...patch } : l))));
  const removeImg = (id: string) =>
    mutate(() => {
      setImgLayers(prev => prev.filter(l => l.id !== id));
      setSelectedId(cur => (cur === id ? null : cur));
    });
  const onPickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setUploadingImg(true);
    try {
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `cover-layers/${mediaId}/${imgLayers.length}_${safe}`;
      const url = await uploadFile(file, path);
      const id = makeId(1000 + imgLayers.length);
      mutate(() => {
        setImgLayers(prev => [...prev, { id, url, xPct: 50, yPct: 50, wPct: 40, rotation: 0, opacity: 1 }]);
        setSelectedId(id);
      });
    } catch (err) {
      console.error("Cover image upload failed", err);
      setSaveError(true);
    } finally {
      setUploadingImg(false);
    }
  };

  // ── Drag (works for text + image layers) ────────────────────────────────────
  const dragRef = useRef<{ id: string; kind: "text" | "image" } | null>(null);
  const startDrag = (id: string, kind: "text" | "image") => (e: React.PointerEvent) => {
    if (!design) return;
    e.stopPropagation();
    e.preventDefault();
    setSelectedId(id);
    dragRef.current = { id, kind };
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  };
  const onLayerPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const r = rootRef.current?.getBoundingClientRect();
    if (!r || !r.width || !r.height) return;
    const x = clamp(((e.clientX - r.left) / r.width) * 100, 0, 100);
    const y = clamp(((e.clientY - r.top) / r.height) * 100, 0, 100);
    if (d.kind === "text") patchLayer(d.id, { xPct: x, yPct: y });
    else patchImg(d.id, { xPct: x, yPct: y });
  };
  const onLayerPointerUp = (e: React.PointerEvent) => {
    if (dragRef.current) {
      (e.currentTarget as Element).releasePointerCapture?.(e.pointerId);
      dragRef.current = null;
    }
  };

  const selected = layers.find(l => l.id === selectedId) || null;
  const selectedImg = imgLayers.find(l => l.id === selectedId) || null;
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  // A listener's personal Artwork FX choice overrides the creator's default —
  // EXCEPT in design mode, where the creator must see exactly what they are
  // programming (a personal pref used to mask the design-panel selection, so
  // picking FX seemed to do nothing).
  const [fxPref] = useArtworkFx(mediaId);
  const effFx = resolveFx(design ? null : fxPref, { effect: fx.effect, intensity: fx.intensity, color: fx.color });

  return (
    <div ref={rootRef} className="absolute inset-0 pointer-events-none" style={{ containerType: "inline-size" }}>
      {/* FX layer — off by default; the creator's setting, or the listener's own
          Artwork FX choice if they picked one. */}
      {effFx.effects && effFx.effects.length > 0 && (
        <AudioReactiveArtwork
          effects={effFx.effects}
          intensity={effFx.intensity}
          color={effFx.color || undefined}
          imageUrl={coverUrl}
          className="absolute inset-0 pointer-events-none z-[1]"
        />
      )}

      {/* Artwork FX control — available to EVERY listener (not just the creator). */}
      <ArtworkFxControl
        trackId={mediaId}
        itemDefault={{ effect: fx.effect, intensity: fx.intensity, color: fx.color }}
        className="absolute bottom-2 right-2 z-[20]"
        openDir="up"
      />

      {/* Design-mode backdrop: taps on empty cover deselect (and never open the
          lightbox) instead of falling through to the page's zoom handler. */}
      {design && (
        <div
          className="absolute inset-0 z-[2] pointer-events-auto"
          onPointerDown={(e) => { e.stopPropagation(); setSelectedId(null); }}
        />
      )}

      {/* Imported image / SVG / sprite layers (below text). Each can have an
          audio-reactive motion mode consuming the shared --bb-* vars. */}
      {imgLayers.filter(l => l.url).map(l => {
        const motion = IMAGE_MOTIONS.find(m => m.id === (l.motion || "none")) || IMAGE_MOTIONS[0];
        return (
        <img
          key={l.id}
          src={l.url}
          alt=""
          draggable={false}
          onPointerDown={startDrag(l.id, "image")}
          onPointerMove={design ? onLayerPointerMove : undefined}
          onPointerUp={design ? onLayerPointerUp : undefined}
          className="absolute z-[3] select-none"
          style={{
            left: `${l.xPct}%`,
            top: `${l.yPct}%`,
            width: `${l.wPct}cqw`,
            height: "auto",
            transform: `translate(-50%, -50%) rotate(${l.rotation}deg)${motion.transform}`,
            opacity: motion.opacity ? (`calc(${l.opacity} * ${motion.opacity})` as any) : l.opacity,
            pointerEvents: design ? "auto" : "none",
            cursor: design ? "move" : "default",
            touchAction: "none",
            outline: design && selectedId === l.id ? "1px dashed rgba(255,255,255,0.9)" : "none",
            outlineOffset: "2px",
            willChange: motion.id !== "none" ? "transform" : undefined,
          }}
        />
        );
      })}

      {/* Text layers (visible to everyone; interactive only in design mode). */}
      {layers.map(l => (
        <div
          key={l.id}
          onPointerDown={startDrag(l.id, "text")}
          onPointerMove={design ? onLayerPointerMove : undefined}
          onPointerUp={design ? onLayerPointerUp : undefined}
          className="absolute z-[4] select-none"
          style={{
            left: `${l.xPct}%`,
            top: `${l.yPct}%`,
            transform: `translate(-50%, -50%) rotate(${l.rotation}deg)`,
            fontSize: `${l.sizePct}cqw`,
            color: l.color,
            fontWeight: l.weight,
            textAlign: l.align,
            fontFamily: FONT_STACKS[l.font],
            lineHeight: 1.05,
            whiteSpace: "pre-wrap",
            maxWidth: "94%",
            textShadow: l.shadow ? "0 2px 10px rgba(0,0,0,0.55), 0 0 2px rgba(0,0,0,0.6)" : "none",
            pointerEvents: design ? "auto" : "none",
            cursor: design ? "move" : "default",
            touchAction: "none",
            outline: design && selectedId === l.id ? "1px dashed rgba(255,255,255,0.9)" : "none",
            outlineOffset: "4px",
            padding: "2px 4px",
          }}
        >
          {l.text || "Text"}
        </div>
      ))}

      {/* Design toggle (admin/creator only). */}
      {canEdit && !design && (
        // Top-right: Edit (the track/release edit window — Gordon
        // 2026-10-31) and Design (cover FX + text layers).
        <div className="absolute top-2 right-2 z-[20] flex items-center gap-1.5 pointer-events-auto">
          {onEdit && (
            <button
              type="button"
              onClick={(e) => { stop(e); onEdit(); }}
              onPointerDown={stop}
              className="flex items-center gap-1 rounded-full bg-black/70 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg backdrop-blur hover:bg-black/85"
              aria-label={editLabel}
              title={editLabel}
            >
              <Pencil className="h-3.5 w-3.5" /> {editLabel}
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { stop(e); setDesign(true); }}
            onPointerDown={stop}
            className="flex items-center gap-1 rounded-full bg-black/70 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg backdrop-blur hover:bg-black/85"
            title="Design cover — FX + text layers"
          >
            <Wand2 className="h-3.5 w-3.5" /> Design
          </button>
        </div>
      )}
      {canEdit && design && (saved || saveError) && (
        <div className={cn(
          "absolute top-2 right-2 z-[20] pointer-events-none flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium text-white shadow",
          saveError ? "bg-red-600" : "bg-emerald-600"
        )}>
          {saveError ? <><X className="h-3 w-3" /> Save failed</> : <><Check className="h-3 w-3" /> Saved</>}
        </div>
      )}

      {/* Control bar (design mode). */}
      {canEdit && design && (
        <div
          className="absolute inset-x-0 bottom-0 z-[20] pointer-events-auto max-h-[62%] overflow-y-auto rounded-t-2xl bg-black/85 p-3 text-white shadow-2xl backdrop-blur-md space-y-3"
          onPointerDown={stop}
          onClick={stop}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-white/70">Cover Design</span>
            <button
              type="button"
              onClick={() => { setDesign(false); setSelectedId(null); }}
              className="flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-semibold text-black hover:bg-white/90"
            >
              <Check className="h-3.5 w-3.5" /> Done
            </button>
          </div>

          {/* Animated FX (vector layer etc.) */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-medium text-white/60">Animated FX (reacts to the audio)</label>
            <div className="flex flex-wrap gap-1.5">
              {ARTWORK_FX.map(o => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => mutate(() => setFx(f => ({ ...f, effect: o.id })))}
                  className={cn(
                    "rounded-full px-2.5 py-1 text-[11px] font-medium",
                    fx.effect === o.id ? "bg-white text-black" : "bg-white/15 text-white hover:bg-white/25"
                  )}
                  title={o.hint}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {fx.effect !== "off" && (
              <div className="flex items-center gap-3 pt-1">
                <div className="flex flex-1 items-center gap-2">
                  <span className="text-[11px] text-white/60">Strength</span>
                  <input
                    type="range" min={0} max={1.5} step={0.05} value={fx.intensity}
                    onChange={(e) => mutate(() => setFx(f => ({ ...f, intensity: parseFloat(e.target.value) })))}
                    className="flex-1 accent-white"
                  />
                  <span className="w-8 text-right text-[11px] text-white/60">{fx.intensity.toFixed(2)}</span>
                </div>
                <label className="flex items-center gap-1 text-[11px] text-white/60">
                  Colour
                  <input
                    type="color" value={fx.color || "#ff3366"}
                    onChange={(e) => mutate(() => setFx(f => ({ ...f, color: e.target.value })))}
                    className="h-6 w-8 cursor-pointer rounded border border-white/20 bg-transparent"
                  />
                </label>
              </div>
            )}
          </div>

          {/* Image / SVG / sprite layers */}
          <div className="space-y-2 border-t border-white/10 pt-2">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-medium text-white/60">Image / SVG / sprite layers — drag to move</label>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploadingImg}
                className="flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-white/25 disabled:opacity-50"
              >
                {uploadingImg ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} Import
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/svg+xml,image/png,image/webp,image/gif,image/jpeg,image/*"
                className="hidden"
                onChange={onPickImage}
              />
            </div>

            {imgLayers.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {imgLayers.map((l, i) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => setSelectedId(l.id)}
                    className={cn(
                      "flex items-center gap-1 rounded px-2 py-1 text-[11px]",
                      selectedId === l.id ? "bg-white text-black" : "bg-white/15 text-white hover:bg-white/25"
                    )}
                  >
                    <ImagePlus className="h-3 w-3" /> #{i + 1}
                  </button>
                ))}
              </div>
            )}

            {selectedImg && (
              <div className="space-y-2 rounded-lg bg-white/5 p-2">
                {/* Motion — how this image moves to the music */}
                <div className="flex items-center gap-2">
                  <span className="w-12 text-[11px] text-white/60">Motion</span>
                  <div className="flex flex-wrap gap-1">
                    {IMAGE_MOTIONS.map(m => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => patchImg(selectedImg.id, { motion: m.id })}
                        className={cn(
                          "rounded-full px-2 py-0.5 text-[11px]",
                          (selectedImg.motion || "none") === m.id ? "bg-white text-black" : "bg-white/15 text-white hover:bg-white/25"
                        )}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-12 text-[11px] text-white/60">Size</span>
                  <input
                    type="range" min={5} max={100} step={1} value={selectedImg.wPct}
                    onChange={(e) => patchImg(selectedImg.id, { wPct: parseFloat(e.target.value) })}
                    className="flex-1 accent-white"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-12 text-[11px] text-white/60">Rotate</span>
                  <input
                    type="range" min={-180} max={180} step={1} value={selectedImg.rotation}
                    onChange={(e) => patchImg(selectedImg.id, { rotation: parseInt(e.target.value, 10) })}
                    className="flex-1 accent-white"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-12 text-[11px] text-white/60">Opacity</span>
                  <input
                    type="range" min={0.1} max={1} step={0.05} value={selectedImg.opacity}
                    onChange={(e) => patchImg(selectedImg.id, { opacity: parseFloat(e.target.value) })}
                    className="flex-1 accent-white"
                  />
                  <button
                    type="button"
                    onClick={() => removeImg(selectedImg.id)}
                    className="flex items-center justify-center rounded bg-red-600 p-1.5 text-white hover:bg-red-500"
                    title="Delete image layer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Text layers */}
          <div className="space-y-2 border-t border-white/10 pt-2">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-medium text-white/60">Text layers — tap to select, drag to move</label>
              <button
                type="button"
                onClick={addLayer}
                className="flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-white/25"
              >
                <Plus className="h-3.5 w-3.5" /> Add text
              </button>
            </div>

            {layers.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {layers.map(l => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => setSelectedId(l.id)}
                    className={cn(
                      "max-w-[9rem] truncate rounded px-2 py-1 text-[11px]",
                      selectedId === l.id ? "bg-white text-black" : "bg-white/15 text-white hover:bg-white/25"
                    )}
                  >
                    <TypeIcon className="mr-1 inline h-3 w-3" />{l.text || "Text"}
                  </button>
                ))}
              </div>
            )}

            {selected && (
              <div className="space-y-2 rounded-lg bg-white/5 p-2">
                <div className="flex items-center gap-2">
                  <input
                    value={selected.text}
                    onChange={(e) => patchLayer(selected.id, { text: e.target.value })}
                    placeholder="Type your text…"
                    className="flex-1 rounded bg-black/40 px-2 py-1 text-sm text-white outline-none ring-1 ring-white/15 focus:ring-white/40"
                  />
                  <button
                    type="button"
                    onClick={() => removeLayer(selected.id)}
                    className="flex items-center justify-center rounded bg-red-600 p-1.5 text-white hover:bg-red-500"
                    title="Delete layer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <span className="w-12 text-[11px] text-white/60">Size</span>
                  <input
                    type="range" min={3} max={30} step={0.5} value={selected.sizePct}
                    onChange={(e) => patchLayer(selected.id, { sizePct: parseFloat(e.target.value) })}
                    className="flex-1 accent-white"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-12 text-[11px] text-white/60">Rotate</span>
                  <input
                    type="range" min={-45} max={45} step={1} value={selected.rotation}
                    onChange={(e) => patchLayer(selected.id, { rotation: parseInt(e.target.value, 10) })}
                    className="flex-1 accent-white"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {/* Colour swatches */}
                  <div className="flex items-center gap-1">
                    {SWATCHES.map(c => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => patchLayer(selected.id, { color: c })}
                        className={cn("h-5 w-5 rounded-full border", selected.color === c ? "border-white" : "border-white/20")}
                        style={{ background: c }}
                        title={c}
                      />
                    ))}
                    <input
                      type="color" value={selected.color}
                      onChange={(e) => patchLayer(selected.id, { color: e.target.value })}
                      className="h-5 w-6 cursor-pointer rounded border border-white/20 bg-transparent"
                    />
                  </div>
                  {/* Weight */}
                  <div className="flex overflow-hidden rounded ring-1 ring-white/15">
                    {[400, 700, 900].map(w => (
                      <button
                        key={w}
                        type="button"
                        onClick={() => patchLayer(selected.id, { weight: w })}
                        className={cn("px-2 py-1 text-[11px]", selected.weight === w ? "bg-white text-black" : "bg-white/10 text-white")}
                        style={{ fontWeight: w }}
                      >
                        {w === 400 ? "Aa" : w === 700 ? "Aa" : "Aa"}
                      </button>
                    ))}
                  </div>
                  {/* Font */}
                  <select
                    value={selected.font}
                    onChange={(e) => patchLayer(selected.id, { font: e.target.value as CoverTextLayer["font"] })}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                        e.preventDefault();
                        const fonts: CoverTextLayer["font"][] = ["display", "sans", "serif", "mono"];
                        const idx = fonts.indexOf(selected.font);
                        const dir = e.key === "ArrowUp" ? -1 : 1;
                        const nextIdx = (idx + dir + fonts.length) % fonts.length;
                        patchLayer(selected.id, { font: fonts[nextIdx] });
                      }
                    }}
                    className="rounded bg-black/40 px-2 py-1 text-[11px] text-white ring-1 ring-white/15 cursor-pointer"
                  >
                    <option value="display">Display</option>
                    <option value="sans">Sans</option>
                    <option value="serif">Serif</option>
                    <option value="mono">Mono</option>
                  </select>
                  {/* Align */}
                  <select
                    value={selected.align}
                    onChange={(e) => patchLayer(selected.id, { align: e.target.value as CoverTextLayer["align"] })}
                    className="rounded bg-black/40 px-2 py-1 text-[11px] text-white ring-1 ring-white/15"
                  >
                    <option value="center">Center</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                  {/* Shadow */}
                  <button
                    type="button"
                    onClick={() => patchLayer(selected.id, { shadow: !selected.shadow })}
                    className={cn("rounded px-2 py-1 text-[11px]", selected.shadow ? "bg-white text-black" : "bg-white/10 text-white")}
                  >
                    Shadow
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function sanitizeLayer(l: any): CoverTextLayer {
  return {
    id: String(l?.id ?? makeId(1)),
    text: typeof l?.text === "string" ? l.text : "",
    xPct: clamp(Number(l?.xPct ?? 50), 0, 100),
    yPct: clamp(Number(l?.yPct ?? 50), 0, 100),
    sizePct: clamp(Number(l?.sizePct ?? 9), 2, 40),
    color: typeof l?.color === "string" ? l.color : "#ffffff",
    weight: [400, 700, 900].includes(Number(l?.weight)) ? Number(l.weight) : 700,
    align: ["left", "center", "right"].includes(l?.align) ? l.align : "center",
    font: ["sans", "serif", "mono", "display"].includes(l?.font) ? l.font : "display",
    shadow: l?.shadow !== false,
    rotation: clamp(Number(l?.rotation ?? 0), -180, 180),
  };
}

function sanitizeImageLayer(l: any): CoverImageLayer {
  return {
    id: String(l?.id ?? makeId(1000)),
    url: typeof l?.url === "string" ? l.url : "",
    xPct: clamp(Number(l?.xPct ?? 50), 0, 100),
    yPct: clamp(Number(l?.yPct ?? 50), 0, 100),
    wPct: clamp(Number(l?.wPct ?? 40), 3, 100),
    rotation: clamp(Number(l?.rotation ?? 0), -180, 180),
    opacity: clamp(Number(l?.opacity ?? 1), 0, 1),
    motion: IMAGE_MOTIONS.some(m => m.id === l?.motion) ? l.motion : "none",
  };
}
